import { useCallback, useEffect, useState } from 'react'
import { Image as ImageIcon, Loader2, Video, Wand2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import { assetDownload, generateAsset, listAssets, type Asset } from './api'

/**
 * Generated media, and its permanent provenance.
 *
 * ## Every asset carries what made it, forever
 *
 * The prompt, the vendor, the model and a disclosure line are on the row, and the
 * synthetic flag has a database CHECK keeping it true. Synthetic political
 * imagery that loses its provenance on the way out of the system is the specific
 * harm this module could do, and a boolean nobody can forget to set is a cheaper
 * guarantee than a convention everybody agrees with.
 *
 * ## The must-not-depict list is applied, not merely displayed
 *
 * Four rules are appended to every prompt by the server regardless of what the
 * brief said: no real person other than the candidate, no ballot papers, no
 * commission branding, no scene implying a declared result. A content brief that
 * listed them and then sent a prompt without them would have documented a rule
 * instead of applying one.
 */
export function StudioPage() {
  const [assets, setAssets] = useState<Asset[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [kind, setKind] = useState<'IMAGE' | 'VIDEO'>('IMAGE')
  const [busy, setBusy] = useState(false)
  const [urls, setUrls] = useState<Record<string, string>>({})
  const mayGenerate = useCan('advisor:generate-media')

  const load = useCallback(async () => {
    try {
      setError(null)
      setAssets(await listAssets(0, 40))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const pending = (assets ?? []).some(
    (a) => a.status === 'QUEUED' || a.status === 'GENERATING')
  useEffect(() => {
    if (!pending) return
    const timer = setInterval(() => { void load() }, 6000)
    return () => clearInterval(timer)
  }, [pending, load])

  const generate = async () => {
    setBusy(true)
    setError(null)
    try {
      await generateAsset({
        kind,
        prompt: prompt.trim(),
        aspectRatio: kind === 'VIDEO' ? '9:16' : '4:5',
        durationSeconds: kind === 'VIDEO' ? 8 : undefined,
      })
      setPrompt('')
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const reveal = async (asset: Asset) => {
    try {
      const link = await assetDownload(asset.id)
      setUrls((current) => ({ ...current, [asset.id]: link.url }))
    } catch (err) {
      setError(explain(err))
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-erp-lg font-semibold">Studio</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Everything here is generated, and says so — on the row, in every export, and under
          every preview.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {mayGenerate ? (
        <section className="rounded-md border border-border p-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {(['IMAGE', 'VIDEO'] as const).map((k) => (
              <button key={k} type="button" onClick={() => setKind(k)}
                className={cn('erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5',
                  kind === k && 'border-chocolate text-chocolate')}>
                {k === 'IMAGE' ? <ImageIcon className="h-3.5 w-3.5" />
                               : <Video className="h-3.5 w-3.5" />}
                {k === 'IMAGE' ? 'Still' : 'Short video'}
              </button>
            ))}
          </div>
          <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the asset. Concrete and visual — this is sent as the prompt."
            className="erp-input w-full text-erp-sm" />
          <p className="text-erp-xs text-muted-foreground">
            The platform appends its own must-not-depict list to whatever you write: no real
            person other than this campaign&rsquo;s candidate, no ballot papers, no electoral
            commission branding, no scene implying a declared result.
          </p>
          <div className="flex justify-end">
            <button type="button" disabled={busy || !prompt.trim()}
              onClick={() => void generate()}
              className="erp-btn erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center
                gap-2">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Wand2 className="h-3.5 w-3.5" />}
              Generate
            </button>
          </div>
        </section>
      ) : (
        <Note tone="info">
          Generating synthetic media needs <code>advisor:generate-media</code>. Content briefs
          are still written by the creative council; nothing is rendered without it.
        </Note>
      )}

      {assets === null && !error && <LoadingBlock />}
      {assets && assets.length === 0 && (
        <Note tone="info">Nothing has been generated on this deployment.</Note>
      )}

      <ul className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {(assets ?? []).map((asset) => (
          <li key={asset.id} className="rounded-md border border-border overflow-hidden">
            <div className="aspect-[4/5] bg-muted/40 grid place-items-center">
              {asset.status === 'READY' ? (
                urls[asset.id] ? (
                  asset.kind === 'IMAGE'
                    ? <img src={urls[asset.id]} alt={asset.prompt.slice(0, 80)}
                        className="h-full w-full object-cover" />
                    : <video src={urls[asset.id]} controls className="h-full w-full" />
                ) : (
                  <button type="button" onClick={() => void reveal(asset)}
                    className="erp-btn h-8 px-2.5 text-erp-xs">Show</button>
                )
              ) : (
                <div className="text-erp-xs text-muted-foreground flex items-center gap-1.5">
                  {(asset.status === 'QUEUED' || asset.status === 'GENERATING') && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  {asset.status === 'FAILED' ? asset.errorMessage : asset.status.toLowerCase()}
                </div>
              )}
            </div>
            <div className="px-2.5 py-2 space-y-1">
              {/* Not decorative. The disclosure travels with the row precisely so it cannot
                  be separated from the picture. */}
              <div className="text-erp-xs font-medium text-amber-800 dark:text-amber-400">
                {asset.disclosureText}
              </div>
              <p className="text-erp-xs text-muted-foreground line-clamp-3">{asset.prompt}</p>
              <div className="text-erp-xs text-muted-foreground/70">
                {asset.vendor} {asset.modelKey} · {fmtDateTime(asset.createdAt)}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
